import { TOPIC } from "@/constants/testIds";
import { ShieldCheck, AlertTriangle, Clock } from "lucide-react";
import { renderWithRedactions } from "./TextFormatter";
import { alignedTimelineFacts, resolvedFactSourceIndex } from "@/lib/factAlignment";

// Veredicto do facto sobre a narrativa (escrita verificada, desde 2026-10-06).
const VERDICTS = {
        confirmed: { label: "Confirma", color: "var(--truth)" },
        refuted: { label: "Desmente", color: "var(--deception)" },
        partial: { label: "Confirma em parte", color: "var(--ai)" },
        unverifiable: { label: "Não verificável", color: "var(--ink-soft)" },
};
// O texto já abre com o veredicto ("Confirma."); com o selo, não se repete.
const VERDICT_PREFIX = /^\s*(Confirma em parte|Confirma|Desmente|Não verificável)\.\s*/i;

export const FactsVsNarrative = ({ facts = [], narratives = [], topic = {} }) => {
        // Verifica se é o novo formato (Timeline) ou o antigo (Strings)
        const isTimelineMode = facts.length > 0 && typeof facts[0] === "object";
        const timelineFacts = isTimelineMode ? alignedTimelineFacts(facts, topic) : facts;
        const sources = topic.sources || [];

        if (isTimelineMode) {
                return (
                        <section className="bg-[var(--paper)] border border-[var(--line-strong)] p-6 md:p-10 relative">
                                <div className="absolute top-4 right-4 stamp text-[9px] py-1 px-2">
                                        Timeline de Provas
                                </div>
                                <div className="flex items-center gap-3 mb-8">
                                        <Clock
                                                size={18}
                                                strokeWidth={1.5}
                                                style={{ color: "var(--ink-soft)" }}
                                        />
                                        <div
                                                className="overline"
                                                style={{ color: "var(--ink-soft)" }}
                                        >
                                                / Cronologia dos Eventos
                                        </div>
                                </div>
                                
                                <div className="relative border-l-2 border-[var(--line-strong)] ml-4 md:ml-6 space-y-10 pb-4">
                                        {timelineFacts.map((f, i) => {
                                                const sourceIndex = resolvedFactSourceIndex(f, sources);
                                                return (
                                                        <div key={i} className="relative pl-8 md:pl-10">
                                                                {/* Timeline Dot */}
                                                                <div className="absolute -left-[7px] top-1.5 w-3 h-3 rounded-full bg-[var(--ink)] border-2 border-[var(--paper)]"></div>
                                                                
                                                                {/* Date */}
                                                                <div className="font-mono text-[11px] text-[var(--ink-faint)] tracking-widest uppercase mb-2">
                                                                        {renderWithRedactions(f.date)}
                                                                </div>

                                                                {/* Cards Container */}
                                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                                        {/* Media Claim */}
                                                                        <div className="border border-[var(--deception)]/20 bg-[var(--deception)]/5 p-4 relative">
                                                                                <div className="flex items-center gap-2 mb-2">
                                                                                        <AlertTriangle size={14} style={{ color: "var(--deception)" }} />
                                                                                        <span className="overline text-[9px]" style={{ color: "var(--deception)" }}>Narrativa</span>
                                                                                </div>
                                                                                <p className="text-[14px] leading-relaxed text-[var(--ink)]">
                                                                                        {renderWithRedactions(f.media_claim)}
                                                                                </p>
                                                                        </div>

                                                                        {/* Truth */}
                                                                        <div className="border border-[var(--truth)]/30 bg-[var(--truth)]/5 p-4 relative">
                                                                                <div className="flex flex-wrap items-center gap-2 mb-2">
                                                                                        <ShieldCheck size={14} style={{ color: "var(--truth)" }} />
                                                                                        <span className="overline text-[9px]" style={{ color: "var(--truth)" }}>Facto Documentado</span>
                                                                                        {VERDICTS[f.verdict] && (
                                                                                                <span
                                                                                                        data-testid="fact-verdict"
                                                                                                        className="stamp text-[9px] py-0.5 px-2"
                                                                                                        style={{ color: VERDICTS[f.verdict].color, borderColor: VERDICTS[f.verdict].color }}
                                                                                                >
                                                                                                        {VERDICTS[f.verdict].label}
                                                                                                </span>
                                                                                        )}
                                                                                </div>
                                                                                <p className="text-[14px] leading-relaxed text-[var(--ink)]">
                                                                                        {renderWithRedactions(VERDICTS[f.verdict] ? String(f.truth || "").replace(VERDICT_PREFIX, "") : f.truth)}
                                                                                </p>
                                                                                {sourceIndex && (
                                                                                        <div className="mt-3 text-[10px] text-[var(--ink-faint)] font-mono">
                                                                                                [Prova Anexada: {sourceIndex}]
                                                                                        </div>
                                                                                )}
                                                                        </div>
                                                                </div>
                                                        </div>
                                                );
                                        })}
                                </div>
                        </section>
                );
        }

        // Legacy Mode
        return (
                <section className="grid grid-cols-1 md:grid-cols-2 gap-0 border border-[var(--line-strong)] bg-[var(--paper)]">
                        <div
                                data-testid={TOPIC.narrativesList}
                                className="p-8 md:p-10 border-b md:border-b-0 md:border-r border-[var(--line-strong)] relative"
                        >
                                <div className="absolute top-4 right-4 stamp text-[9px] py-1 px-2">
                                        Suspeita
                                </div>
                                <div className="flex items-center gap-3 mb-4">
                                        <AlertTriangle
                                                size={18}
                                                strokeWidth={1.5}
                                                style={{ color: "var(--deception)" }}
                                        />
                                        <div
                                                className="overline"
                                                style={{ color: "var(--deception)" }}
                                        >
                                                / Em circulação
                                        </div>
                                </div>
                                <h3 className="font-serif text-2xl md:text-3xl leading-tight mb-6 text-[var(--ink)]">
                                        Narrativas.
                                        <br />
                                        <span className="text-[var(--ink-soft)] italic font-normal">
                                                A versão oficial / mediática.
                                        </span>
                                </h3>
                                <ul className="space-y-4">
                                        {narratives.length === 0 && (
                                                <li className="text-sm text-[var(--ink-faint)] italic">
                                                        Sem narrativas catalogadas.
                                                </li>
                                        )}
                                        {narratives.map((n, i) => (
                                                <li
                                                        key={n}
                                                        className="flex gap-4 text-[15px] leading-relaxed text-[var(--ink)]"
                                                >
                                                        <span
                                                                className="line-no mt-[7px] flex-shrink-0"
                                                                style={{ color: "var(--deception)" }}
                                                        >
                                                                N.{String(i + 1).padStart(2, "0")}
                                                        </span>
                                                        <span>{renderWithRedactions(n)}</span>
                                                </li>
                                        ))}
                                </ul>
                        </div>

                        <div
                                data-testid={TOPIC.factsList}
                                className="p-8 md:p-10 relative"
                        >
                                <div className="absolute top-4 right-4 stamp stamp-truth text-[9px] py-1 px-2">
                                        Verificado
                                </div>
                                <div className="flex items-center gap-3 mb-4">
                                        <ShieldCheck
                                                size={18}
                                                strokeWidth={1.5}
                                                style={{ color: "var(--truth)" }}
                                        />
                                        <div
                                                className="overline"
                                                style={{ color: "var(--truth)" }}
                                        >
                                                / Documentado
                                        </div>
                                </div>
                                <h3 className="font-serif text-2xl md:text-3xl leading-tight mb-6 text-[var(--ink)]">
                                        Factos verificados.
                                        <br />
                                        <span className="text-[var(--ink-soft)] italic font-normal">
                                                O que é demonstrável.
                                        </span>
                                </h3>
                                <ul className="space-y-4">
                                        {facts.length === 0 && (
                                                <li className="text-sm text-[var(--ink-faint)] italic">
                                                        Sem factos catalogados.
                                                </li>
                                        )}
                                        {facts.map((f, i) => (
                                                <li
                                                        key={f}
                                                        className="flex gap-4 text-[15px] leading-relaxed text-[var(--ink)]"
                                                >
                                                        <span
                                                                className="line-no mt-[7px] flex-shrink-0"
                                                                style={{ color: "var(--truth)" }}
                                                        >
                                                                F.{String(i + 1).padStart(2, "0")}
                                                        </span>
                                                        <span>{renderWithRedactions(f)}</span>
                                                </li>
                                        ))}
                                </ul>
                        </div>
                </section>
        );
};

export default FactsVsNarrative;
